// domain/gaji.ts — perhitungan slip gaji (murni, diuji).
//
// Aturan yang dijaga di sini:
//  · Netto = (pokok + tunjangan + komisi) − potongan, TIDAK PERNAH negatif. Gaji minus berarti
//    karyawan berutang ke toko — itu keputusan orang, bukan hasil hitungan otomatis. Sisanya
//    dilaporkan terpisah supaya tak hilang diam-diam.
//  · Potongan kehadiran memakai TARIF yang disetel, dan tarif kosong (0) = tidak memotong apa
//    pun. Jadi menu ini tak mengubah gaji siapa pun sampai Apo sengaja mengisi tarifnya.

export interface Komponen { nama: string; jumlah: number }

/** Tarif potongan kehadiran. Semua default 0 → aplikasi tidak memotong sampai diisi. */
export interface TarifPotongan {
  /** Per kejadian telat. */
  telat: number;
  /** Per kejadian ½ hari. */
  setengah: number;
  /**
   * Per hari alpha. `-1` berarti "sehari gaji pokok" — dihitung dari gaji pokok ÷ hari kerja,
   * cara yang paling sering dipakai toko dan tak perlu disetel ulang tiap gaji naik.
   */
  alpha: number;
  /** Per menit kelebihan istirahat. */
  istirahatPerMenit: number;
  /**
   * Hari kerja sebulan — penyebut "sehari gaji". Angka TETAP yang disetel, bukan diturunkan dari
   * catatan absensi: bulan dengan absensi tak lengkap akan menghasilkan penyebut kecil, dan
   * potongan sehari-gaji jadi berlipat tanpa ada yang menyadari sebabnya.
   */
  hariKerjaSebulan: number;
}

export const TARIF_KOSONG: TarifPotongan = { telat: 0, setengah: 0, alpha: 0, istirahatPerMenit: 0, hariKerjaSebulan: 26 };

/** Kehadiran yang dipakai memotong — bentuk minimal dari `rekapKehadiran`. */
export interface KehadiranGaji { telat: number; setengah: number; alpha: number; istLebih: number }

const bulat = (x: number): number => Math.max(0, Math.round(x || 0));

/** Gaji sehari = pokok ÷ hari kerja efektif. Hari kerja 0 → 0 (jangan bagi nol). */
export function gajiSehari(gajiPokok: number, hariKerja: number): number {
  return hariKerja > 0 ? Math.round(gajiPokok / hariKerja) : 0;
}

/**
 * Potongan kehadiran sebagai daftar komponen bernama — bukan satu angka gelondongan, supaya
 * karyawan yang menerima slip bisa melihat potongannya dari mana.
 * Komponen bernilai 0 tidak dimunculkan: baris "Telat: Rp 0" hanya bikin slip panjang.
 */
export function potonganKehadiran(
  k: KehadiranGaji, tarif: TarifPotongan, gajiPokok: number, hariKerja: number,
): Komponen[] {
  const out: Komponen[] = [];
  const tarifAlpha = tarif.alpha === -1 ? gajiSehari(gajiPokok, hariKerja) : tarif.alpha;
  if (k.telat > 0 && tarif.telat > 0) out.push({ nama: `Telat ${k.telat}×`, jumlah: bulat(k.telat * tarif.telat) });
  if (k.setengah > 0 && tarif.setengah > 0) out.push({ nama: `½ hari ${k.setengah}×`, jumlah: bulat(k.setengah * tarif.setengah) });
  if (k.alpha > 0 && tarifAlpha > 0) out.push({ nama: `Alpha ${k.alpha} hari`, jumlah: bulat(k.alpha * tarifAlpha) });
  if (k.istLebih > 0 && tarif.istirahatPerMenit > 0) out.push({ nama: `Istirahat lewat ${k.istLebih} mnt`, jumlah: bulat(k.istLebih * tarif.istirahatPerMenit) });
  return out;
}

export const jumlahKomponen = (list: Komponen[] | null | undefined): number =>
  (list ?? []).reduce((a, c) => a + bulat(c.jumlah), 0);

export interface HasilSlip {
  bruto: number; totalPotongan: number; netto: number;
  /** Potongan yang TIDAK termuat karena netto sudah nol — dilaporkan, bukan dibuang diam-diam. */
  sisaPotongan: number;
}

/** Perhitungan akhir slip. Netto tak pernah minus; kelebihan potongan dilaporkan terpisah. */
export function hitungSlip(gajiPokok: number, tunjangan: Komponen[], komisi: number, potongan: Komponen[]): HasilSlip {
  const bruto = bulat(gajiPokok) + jumlahKomponen(tunjangan) + bulat(komisi);
  const totalPotongan = jumlahKomponen(potongan);
  const netto = Math.max(0, bruto - totalPotongan);
  return { bruto, totalPotongan, netto, sisaPotongan: Math.max(0, totalPotongan - bruto) };
}
